// @flow
import React, { useState } from 'react';
import injectSheet from 'react-jss';
import moment from 'moment';
import { DEPLOYMENT_TYPES } from 'shared/constants';
import getDisplayPath from 'shared/lib/getDisplayPath';
import { Table, SkeletonRows } from '~/app/ui/Table';
import EmptyState from '~/app/ui/EmptyState';
import StatusBadge from '~/app/ui/StatusBadge';
import { S, A, DARK } from '~/app/ui/tokens';

type Props = {
  classes: Object,
  isFetching: boolean,
  deploymentHistory: Array<Object>
};

const TYPES = {
  [DEPLOYMENT_TYPES.PUBLISH]: { label: 'Publish', tone: 'green' },
  [DEPLOYMENT_TYPES.REDEPLOY]: { label: 'Redeploy', tone: 'blue' },
  [DEPLOYMENT_TYPES.WITHDRAW]: { label: 'Withdraw', tone: 'amber' },
  [DEPLOYMENT_TYPES.DELETE]: { label: 'Delete', tone: 'red' }
};

const pathOf = (path) => {
  const shown = getDisplayPath(path);
  if (!shown || shown.endsWith('/')) return shown;
  return `${shown}/`;
};

const Change = ({ page, c }) => {
  const type = TYPES[page.deploymentType];
  return (
    <div className={c.change}>
      {type && <StatusBadge tone={type.tone} label={type.label} />}
      <span className={c.path} title={pathOf(page.path)}>{pathOf(page.path)}</span>
    </div>
  );
};

/** Past deployments, newest first. A deployment of several pages opens to list them. */
const DeploymentHistory = ({ classes: c, deploymentHistory, isFetching }: Props) => {
  const [open, setOpen] = useState([]);
  const toggle = id => setOpen(o => (o.includes(id) ? o.filter(x => x !== id) : [...o, id]));
  const rows = deploymentHistory || [];

  let body;
  if (isFetching && rows.length === 0) {
    body = <SkeletonRows rows={5} widths={[90, 60, 90, '70%', 70]} />;
  } else if (rows.length === 0) {
    body = <tr><td colSpan={5}><EmptyState icon="rocket_launch" title="No deployments yet" compact /></td></tr>;
  } else {
    body = rows.map((d) => {
      const at = moment(d.timestamp, 'x');
      const pages = (d.data && d.data.pages) || [];
      const id = d._id || d.version;
      const isOpen = open.includes(id);
      return (
        <tr key={id} data-deployment={d.version} className={c.row}>
          <td className={c.when} title={at.format('dddd, MMMM D, YYYY h:mm A')}>
            {moment().diff(at, 'days') < 7 ? at.fromNow() : at.format(at.year() === moment().year() ? 'MMM D, h:mm A' : 'MMM D, YYYY')}
          </td>
          <td className={`${c.version} ${c.mono}`}>
            v{d.version}
            {d.isRollback && <span className={c.tag}>Rollback</span>}
          </td>
          <td className={c.who}>{d.deployedBy && d.deployedBy.username}</td>
          <td>
            {pages.length === 0 && <span className={c.muted}>—</span>}
            {pages[0] && <Change page={pages[0]} c={c} />}
            {pages.length > 1 && (
              <>
                {isOpen && (
                  <div className={c.more}>
                    {pages.slice(1).map((p, i) => <Change key={`${p.path}-${i}`} page={p} c={c} />)}
                  </div>
                )}
                <button type="button" className={c.toggle} onClick={() => toggle(id)} aria-expanded={isOpen}>
                  {isOpen ? 'Show less' : `+ ${pages.length - 1} more ${pages.length - 1 === 1 ? 'page' : 'pages'}`}
                </button>
              </>
            )}
          </td>
          <td className={`${c.commit} ${c.monoSmall}`}>{d.gitCommitSHA}</td>
        </tr>
      );
    });
  }

  return (
    <Table label="Deployment history" testId="deployment-history">
      <thead>
        <tr>
          <th className={c.when}>When</th>
          <th className={c.version}>Version</th>
          <th className={c.who}>By</th>
          <th>Pages</th>
          <th className={c.commit}>Commit</th>
        </tr>
      </thead>
      <tbody>{body}</tbody>
    </Table>
  );
};

const styles = {
  // Several pages make a tall row; the other cells stay at its top.
  row: { '& td': { verticalAlign: 'top' } },
  when: { width: 150, whiteSpace: 'nowrap' },
  version: { width: 120, whiteSpace: 'nowrap' },
  mono: { fontFamily: S.mono, fontSize: 13 },
  monoSmall: { fontFamily: S.mono, fontSize: 12, color: S.muted },
  who: { width: 150, '@media (max-width: 900px)': { display: 'none' } },
  commit: { width: 110, '@media (max-width: 1100px)': { display: 'none' } },
  tag: {
    marginLeft: 8,
    padding: '1px 6px',
    border: `1px solid ${S.fieldLine}`,
    borderRadius: 4,
    fontFamily: S.label,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: S.muted
  },
  change: { display: 'flex', alignItems: 'center', gap: 10, minHeight: 24, minWidth: 0 },
  path: { fontFamily: S.mono, fontSize: 12, color: '#3A3D44', wordBreak: 'break-all', [DARK]: { color: S.fg } },
  more: { display: 'grid', gap: 6, marginTop: 6, maxHeight: 280, overflowY: 'auto' },
  toggle: {
    all: 'unset',
    marginTop: 6,
    fontSize: 13,
    fontWeight: 600,
    color: S.muted,
    cursor: 'pointer',
    '&:hover': { color: S.fg },
    '&:focus-visible': A.focus
  },
  muted: { color: S.muted }
};

export default injectSheet(styles)(DeploymentHistory);
